// Desktop applies immediately. Mobile uses Apply; Clear refreshes the filter form only.
class CollectionBrowse extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    const listen = (target, type, handler, options={}) => target?.addEventListener(type, handler, {...options, signal});
    this.mobile = window.matchMedia('(max-width: 989px)');
    this.toggle = this.querySelector('[data-filter-toggle]');
    this.sidebar = this.querySelector('.cb-sidebar');
    this.backdrop = this.querySelector('.cb-backdrop');
    const menu = this.querySelector('[data-sort-menu]');
    this.filterOrder ||= this.captureFilterOrder();
    this.syncMode();

    let collectionHistoryUrl =
      window.location.pathname + window.location.search;

    listen(window, 'popstate', () => {
      const url = new URL(window.location.href);
      const nextHistoryUrl = url.pathname + url.search;

      // Opening or closing the cart does not change
      // the collection path or filter parameters.
      if (nextHistoryUrl === collectionHistoryUrl) return;

      collectionHistoryUrl = nextHistoryUrl;
      this.updateCollection(url, false);
    });
    listen(this, 'change', event => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      const form = input.closest('[data-filter-form]');
      if (form && input.matches('input[type="checkbox"], [data-price-min], [data-price-max], [data-price-min-range], [data-price-max-range]')) {
        if (this.mobile.matches) this.previewFilters(form);
        else form.requestSubmit();
      }
    });
    // Mobile Clear resets draft selections and requests fresh filter counts.
    listen(this, 'click', event => {
      const button = event.target.closest('[data-mobile-clear]');
      if (!button) return;

      const form = button.closest('[data-filter-form]');
      if (!form) return;

      form.querySelectorAll('input[type="checkbox"]').forEach(input => {
        input.checked = false;
      });

      form.querySelectorAll('[data-price-range]').forEach(range => {
        const low = range.querySelector('[data-price-min]');
        const high = range.querySelector('[data-price-max]');
        low.value = '';
        high.value = '';
        low.dispatchEvent(new Event('input', { bubbles: true }));
      });
      const url = new URL(window.location.href);
      for (const key of [...url.searchParams.keys()]) {
        if (key.startsWith('filter.') || key === 'page') url.searchParams.delete(key);
      }
      url.hash = '';
      this.updateCollection(url);
    });

    const updateFilterLabel = () => {
      const label = this.querySelector('[data-filter-label]');
      if (label) label.textContent = this.mobile.matches ? 'Filter' : this.toggle?.getAttribute('aria-expanded') === 'true' ? 'Close filter' : 'Open filter';
    };
    this.labelObserver = new MutationObserver(updateFilterLabel);
    if (this.toggle) this.labelObserver.observe(this.toggle, { attributes: true, attributeFilter: ['aria-expanded'] });
    updateFilterLabel();
    this.querySelectorAll('[data-clear-filters]').forEach(link => listen(link, 'click', event => {
      event.preventDefault();
      const url = new URL(window.location.href);
      url.pathname = new URL(link.href).pathname;
      for (const key of [...url.searchParams.keys()]) if (key.startsWith('filter.') || key === 'page') url.searchParams.delete(key);
      url.hash = '';
      this.updateCollection(url);
    }));
    listen(this.mobile, 'change', () => { this.closeDrawer(false); this.syncMode(); });
    listen(this.toggle, 'click', () => {
      if (this.mobile.matches) {
        this.classList.contains('filters-open') ? this.closeDrawer() : this.openDrawer();
      } else {
        const collapsed = this.classList.toggle('filters-collapsed');
        this.toggle.setAttribute('aria-expanded', String(!collapsed));
      }
    });
    this.querySelectorAll('[data-filter-close]').forEach(button => listen(button, 'click', () => this.closeDrawer()));
    listen(document, 'click', event => { if (menu && !menu.contains(event.target)) menu.open=false; });
    listen(this, 'keydown', event => {
      if (event.key === 'Escape') {
        if (this.classList.contains('filters-open')) { this.closeDrawer(); event.preventDefault(); }
        else if (menu?.open) { menu.open=false; menu.querySelector('summary').focus(); event.preventDefault(); }
      }
      if (event.key !== 'Tab' || !this.classList.contains('filters-open')) return;
      const targets=[...this.sidebar.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),summary')].filter(el=>el.getClientRects().length);
      const first=targets[0], last=targets.at(-1);
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement===first || document.activeElement===this.sidebar)) { last.focus(); event.preventDefault(); }
      else if (!event.shiftKey && document.activeElement===last) { first.focus(); event.preventDefault(); }
    });
    listen(this, 'input', event => {
      const range = event.target.closest('[data-price-range]');
      if (!range) return;
      const low=range.querySelector('[data-price-min]'), high=range.querySelector('[data-price-max]');
      const lowSlider=range.querySelector('[data-price-min-range]'), highSlider=range.querySelector('[data-price-max-range]');
      const sync=(target)=>{
        let lo=low.value==='' ? 0 : Number(low.value);
        let hi=high.value==='' ? Number(high.max) : Number(high.value);
        if(target===lowSlider) lo=Math.min(Number(lowSlider.value),hi);
        if(target===highSlider) hi=Math.max(Number(highSlider.value),lo);
        if(target===lowSlider) low.value=String(lo);
        if(target===highSlider) high.value=String(hi);
        lowSlider.value=String(lo); highSlider.value=String(hi);
        range.querySelector('[data-price-min-label]').textContent=String(lo);
        range.querySelector('[data-price-max-label]').textContent=String(hi);
        high.setCustomValidity(lo>hi ? 'Maximum price must be at least the minimum price.' : '');
      };
      sync(event.target);
    });
    listen(this,'submit',event=>{
      const form=event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-filter-form],[data-sort-form]')) return;
      event.preventDefault();
      if (!form.reportValidity()) return;
      const data=new FormData(form);
      if(event.submitter?.name) data.set(event.submitter.name,event.submitter.value);
      const url=new URL(window.location.href);
      for(const key of [...url.searchParams.keys()]) if(key.startsWith('filter.') || key==='sort_by' || key==='page') url.searchParams.delete(key);
      for(const [key,value] of data) if(String(value).trim()!=='') url.searchParams.append(key,String(value));
      url.hash='';
      this.updateCollection(url);
    });
    // Keep the selected label truthful when a collection had an old unsupported default sort.
    if(this.dataset.sortSupported==='false' && !window.Shopify?.designMode) {
      const url=new URL(window.location.href);
      url.searchParams.set('sort_by','manual');
      url.searchParams.delete('page');
      window.location.replace(url.toString());
    }
  }
  captureFilterOrder() {
    const order = new Map();
    this.querySelectorAll('.cb-filter-value input').forEach(input => {
      if (!order.has(input.name)) order.set(input.name, []);
      order.get(input.name).push(input.value);
    });
    return order;
  }
  restoreFilterOrder(root) {
    root.querySelectorAll('.cb-facet-values').forEach(container => {
      const labels = [...container.children].filter(el => el.matches('.cb-filter-value'));
      if (!labels.length) return;
      const name = labels[0].querySelector('input')?.name;
      const order = this.filterOrder.get(name) || [];
      labels.forEach(label => {
        const value = label.querySelector('input')?.value;
        if (!order.includes(value)) order.push(value);
      });
      this.filterOrder.set(name, order);
      labels.sort((a, b) => order.indexOf(a.querySelector('input').value) - order.indexOf(b.querySelector('input').value));
      labels.forEach(label => container.append(label));
    });
  }
  lockFilters() {
    this.releaseFilterLock?.();
    this.querySelector('[data-filter-error]')?.remove();
    const controls = [...this.querySelectorAll('[data-filter-form] input, [data-filter-form] button, [data-sort-form] button')];
    const states = controls.map(el => [el, el.disabled]);
    const labels = [...this.querySelectorAll('.cb-filter-value')].filter(label => !label.querySelector('input')?.checked);
    const styles = labels.map(el => [el, el.style.opacity]);
    const selectedLabels = [
  ...this.querySelectorAll('.cb-filter-value')
].filter(label => label.querySelector('input')?.checked);

const inertStates = selectedLabels.map(label => [label, label.inert]);

selectedLabels.forEach(label => {
  label.inert = true;
});

controls.forEach(el => {
  if (el.matches('input[type="checkbox"]') && el.checked) return;
  el.disabled = true;
});
    labels.forEach(el => { el.style.opacity = '0.4'; });
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      states.forEach(([el, disabled]) => {
  el.disabled = disabled;
});

inertStates.forEach(([label, inert]) => {
  label.inert = inert;
});
      styles.forEach(([el, opacity]) => { el.style.opacity = opacity; });
      if (this.releaseFilterLock === release) this.releaseFilterLock = null;
    };
    this.releaseFilterLock = release;
    return release;
  }
  showFilterError(form) {
    let message = form.querySelector('[data-filter-error]');
    if (!message) {
      message = document.createElement('p');
      message.dataset.filterError = '';
      message.setAttribute('role', 'alert');
      form.append(message);
    }
    message.textContent = 'Could not update filters. Please try again.';
  }

  async previewFilters(form) {
    this.previewRequest?.abort();
    const request = new AbortController();
    const timeout = window.setTimeout(() => {
      request.timedOut = true;
      request.abort();
    }, 15000);
    this.previewRequest = request;
    const url = new URL(window.location.href);
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') url.searchParams.delete(key);
    }
    for (const [key, value] of new FormData(form)) {
      if (String(value).trim() !== '') url.searchParams.append(key, String(value));
    }
    url.hash = '';
    form.setAttribute('aria-busy', 'true');
    const unlock = this.lockFilters();
    try {
      const response = await fetch(url.toString(), { signal: request.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Filter preview failed');
      const html = await response.text();
      if (request.signal.aborted || !this.isConnected || !this.classList.contains('filters-open')) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = [...doc.querySelectorAll('collection-browse')].find(el => el.dataset.sectionId === this.dataset.sectionId);
      const nextForm = section?.querySelector('[data-filter-form]');
      if (!nextForm) throw new Error('Filter form missing');
      const groups = new Map([...form.querySelectorAll('.cb-facet')].map(group => [group.querySelector('summary')?.textContent.trim(), group.open]));
      nextForm.querySelectorAll('.cb-facet').forEach(group => {
        const key = group.querySelector('summary')?.textContent.trim();
        if (groups.has(key)) group.open = groups.get(key);
      });
      const y = form.scrollTop;
      const sidebarY = this.sidebar?.scrollTop || 0;
      const focused = document.activeElement;
      const name = focused?.getAttribute('name');
      const value = focused?.value;
      const cleared = focused?.matches('[data-mobile-clear]');
      // Only the draft filter form changes. Product results and URL remain applied state.
      this.restoreFilterOrder(nextForm);
      form.innerHTML = nextForm.innerHTML;
      form.scrollTop = y;
      if (this.sidebar) this.sidebar.scrollTop = sidebarY;
      const focusTarget = cleared ? form.querySelector('[data-mobile-clear]') : [...form.querySelectorAll('input[name]')].find(input => input.name === name && input.value === value);
      focusTarget?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError' && !request.timedOut) return;
      this.showFilterError(form);
    } finally {
      window.clearTimeout(timeout);
      unlock();
      if (this.previewRequest === request) form.removeAttribute('aria-busy');
    }
  }

  async updateCollection(url, pushHistory = true) {
    this.previewRequest?.abort();
    this.filterRequest?.abort();
    const request = new AbortController();
    const timeout = window.setTimeout(() => {
      request.timedOut = true;
      request.abort();
    }, 15000);
    this.filterRequest = request;
    this.setAttribute('aria-busy', 'true');
    const unlock = this.lockFilters();

    try {
      const response = await fetch(url.toString(), {
        signal: request.signal,
        credentials: 'same-origin'
      });

      if (!response.ok) throw new Error('Collection request failed');

      const html = await response.text();
      if (request.signal.aborted || !this.isConnected) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = [...doc.querySelectorAll('collection-browse')]
        .find(element => element.dataset.sectionId === this.dataset.sectionId);

      if (!next) throw new Error('Collection section missing');

      // 在响应到达时记录位置，避免覆盖等待期间的滚动。
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      const sidebarScroll = this.sidebar?.scrollTop || 0;
      const collapsed = this.classList.contains('filters-collapsed');
      const drawerWasOpen = this.classList.contains('filters-open');

      // 保留各筛选分组的展开/关闭状态。
      const groups = new Map(
        [...this.querySelectorAll('.cb-facet')].map(group => [
          group.querySelector('summary')?.textContent.trim(),
          group.open
        ])
      );

      next.querySelectorAll('.cb-facet').forEach(group => {
        const key = group.querySelector('summary')?.textContent.trim();
        if (groups.has(key)) group.open = groups.get(key);
      });

      // 保留桌面键盘操作焦点。
      const focused = document.activeElement;
      const focusName = focused?.getAttribute('name');
      const focusValue = focused?.value;
      const wasToggle = focused === this.toggle;

      this.closeDrawer(false);
      next.removeAttribute('aria-busy');

      if (pushHistory && url.toString() !== window.location.href) {
        window.history.pushState(null, '', url.toString());
      }

      // Preserve the displayed order through every AJAX replacement.
      this.restoreFilterOrder(next);
      next.filterOrder = this.filterOrder;
      this.replaceWith(next);

      // 自定义元素插入后重新绑定原有交互。
      next.classList.toggle(
        'filters-collapsed',
        collapsed && !next.mobile.matches
      );

      next.toggle?.setAttribute(
        'aria-expanded',
        String(!next.mobile.matches && !collapsed)
      );

      if (next.sidebar) next.sidebar.scrollTop = sidebarScroll;

      let focusTarget;
      if (drawerWasOpen || wasToggle) {
        focusTarget = next.toggle;
      } else if (focusName) {
        focusTarget = [...next.querySelectorAll('input[name]')]
          .find(input =>
            input.name === focusName &&
            input.value === focusValue
          );
      }

      focusTarget?.focus({ preventScroll: true });
      window.scrollTo({
        left: scrollX,
        top: scrollY,
        behavior: 'instant'
      });
    } catch (error) {
      if (error.name === 'AbortError' && !request.timedOut) return;

      // 请求失败时才使用普通页面跳转。
      window.location.assign(url.toString());
    } finally {
      window.clearTimeout(timeout);
      unlock();
      if (this.filterRequest === request) {
        this.removeAttribute('aria-busy');
      }
    }
  }

  syncMode() {
    this.classList.remove('filters-collapsed');
    this.toggle?.setAttribute('aria-expanded',String(!this.mobile.matches));
  }
  openDrawer() {
    if(!this.sidebar) return;
    const form = this.querySelector('[data-filter-form]');
    this.appliedFormHTML = form?.innerHTML;
    this.classList.add('filters-open');
    this.toggle?.setAttribute('aria-expanded','true');
    this.backdrop.hidden=false;
    this.sidebar.setAttribute('role','dialog');
    this.sidebar.setAttribute('aria-modal','true');
    this.oldOverflow=document.body.style.overflow;
    document.body.style.overflow='hidden';
    this.sidebar.focus();
  }
  closeDrawer(restoreFocus=true) {
    if(!this.classList.contains('filters-open')) return;
    this.previewRequest?.abort();
    this.releaseFilterLock?.();
    const form = this.querySelector('[data-filter-form]');
    if (form && this.appliedFormHTML != null) form.innerHTML = this.appliedFormHTML;
    form?.removeAttribute('aria-busy');
    this.classList.remove('filters-open');
    this.backdrop.hidden=true;
    this.sidebar.removeAttribute('role');
    this.sidebar.removeAttribute('aria-modal');
    this.toggle?.setAttribute('aria-expanded','false');
    document.body.style.overflow=this.oldOverflow || '';
    if(restoreFocus) this.toggle?.focus();
  }
  disconnectedCallback() {
    this.releaseFilterLock?.();
    this.previewRequest?.abort();
    this.filterRequest?.abort();
    this.labelObserver?.disconnect();
    this.closeDrawer(false);
    this.controller?.abort();
    this.controller=null;
  }
}
if(!customElements.get('collection-browse')) customElements.define('collection-browse',CollectionBrowse);
